import { useState } from 'react'
import { Plus } from 'lucide-react'

import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'
import { ErpTypeahead } from '@/features/sales-order/components/ErpTypeahead'
import { searchErpArticles } from '@/services/api'
import type { NewCustomerOrderRow } from '@/types/salesOrder'

interface PickedArticle {
  number: string
  description: string
}

/**
 * Adds a line to the order, for an article the customer asked for that their document did not
 * carry — or did not carry in a form we could read.
 *
 * The article is the only thing asked for. Everything else is what the document would have stated,
 * and somebody adding a line often knows the article and nothing more, so each field says what
 * happens when it is left alone rather than refusing to go on without it.
 */
export function AddRowPopover({
  workflowId,
  onAdd,
  adding,
}: {
  workflowId: string
  onAdd: (row: NewCustomerOrderRow) => void
  adding: boolean
}) {
  const [open, setOpen] = useState(false)
  const [article, setArticle] = useState<PickedArticle | null>(null)
  const [position, setPosition] = useState('')
  const [deliveryDate, setDeliveryDate] = useState('')
  const [quantity, setQuantity] = useState('')

  function reset() {
    setArticle(null)
    setPosition('')
    setDeliveryDate('')
    setQuantity('')
  }

  function submit() {
    if (!article) return

    const row: NewCustomerOrderRow = { article_number: article.number }
    if (position.trim()) row.position = position.trim()
    if (deliveryDate) row.requested_delivery_date = deliveryDate
    const parsedQuantity = Number(quantity)
    if (quantity.trim() && Number.isFinite(parsedQuantity) && parsedQuantity > 0) {
      row.quantity = parsedQuantity
    }

    onAdd(row)
    reset()
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) reset()
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          Add article
        </Button>
      </PopoverTrigger>
      {/*
        Wider than a popover usually is, because the article search hangs off the field inside it
        and the list is the article number plus its description. At the default width a description
        is cut off at the point where it stops being the thing you are choosing between.
      */}
      <PopoverContent
        align="start"
        className="w-[28rem] max-w-[calc(100vw-2rem)] space-y-4"
        // The article search puts its own list over this one; closing on a click inside that list
        // would take the popover down before the pick landed.
        onInteractOutside={(event) => {
          if (adding) event.preventDefault()
        }}
      >
        <p className="text-sm font-semibold text-gray-900">Add row item</p>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-gray-600">Article</label>
          <div className="rounded-md border border-input px-2 py-1.5">
            <ErpTypeahead
              value={article?.number ?? ''}
              onPick={(suggestion) =>
                setArticle({ number: suggestion.value, description: suggestion.description })
              }
              placeholder="Start typing to find an article"
              label="article"
              queryKey={['erp-article-search', workflowId]}
              enabled={!!workflowId}
              renderResting={(number) => (
                <span className="min-w-0 flex-1">
                  {number ? (
                    <>
                      <span className="block truncate text-sm text-gray-900">{number}</span>
                      {article?.description && (
                        <span className="block truncate text-xs text-gray-500">
                          {article.description}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="block truncate text-sm text-gray-400">
                      Start typing to find an article
                    </span>
                  )}
                </span>
              )}
              search={async (query, source) => {
                const results = await searchErpArticles(workflowId, query, source)
                return results.map((result) => ({
                  value: result.article_number,
                  description: result.description,
                }))
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label htmlFor="add-row-position" className="text-xs font-medium text-gray-600">
              Position
            </label>
            <Input
              id="add-row-position"
              value={position}
              onChange={(event) => setPosition(event.target.value)}
              placeholder="Follows the order"
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="add-row-quantity" className="text-xs font-medium text-gray-600">
              Quantity
            </label>
            <Input
              id="add-row-quantity"
              type="number"
              min="0"
              step="any"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              placeholder="1"
              className="h-9"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="add-row-date" className="text-xs font-medium text-gray-600">
            Delivery date
          </label>
          <Input
            id="add-row-date"
            type="date"
            value={deliveryDate}
            onChange={(event) => setDeliveryDate(event.target.value)}
            className="h-9"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button type="button" size="sm" disabled={!article || adding} onClick={submit}>
            {adding ? 'Adding…' : 'Add'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
